import React, { ReactNode, useCallback, useMemo } from 'react';
import { OutlineTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import cn from 'classnames';

export type ButtonGroupOption = {
  text: ReactNode;
  value: string;
  tooltip?: ReactNode;
  selected: boolean;
};

type ButtonGroupProps = {
  options: ButtonGroupOption[];
  initialSelectedIndex?: number;
  onChange: (selected: string) => void;
};

export const ButtonGroup = ({ options, onChange }: ButtonGroupProps) => {
  const handleOnChange = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue);
    },
    [onChange],
  );

  const buttons = useMemo(
    () =>
      options.map(({ text, tooltip, value, selected }, index) => {
        const button = (
          <button
            key={value}
            className={cn([
              '!px-3 h-10 leading-5 text-xs text-gray-900 bg-white border-gray-200 border',
              '-ml-px',
              '-mt-px',
              {
                '!bg-royal-100 font-bold': selected,
                'font-normal': !selected,
                'rounded-s': index === 0,
                'rounded-e': index === options.length - 1,
              },
            ])}
            onClick={() => handleOnChange(value)}
          >
            <span>{text}</span>
          </button>
        );

        // despite having the placement set to right-end the tooltip is still placed at the top. PopperProps overrides
        // this automatic behavior
        return tooltip ? (
          <OutlineTooltip
            key={value}
            title={tooltip}
            followCursor
            placement="right-end"
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [100, 0],
                  },
                },
              ],
            }}
          >
            {button}
          </OutlineTooltip>
        ) : (
          button
        );
      }),
    [handleOnChange, options],
  );

  return (
    <div className="inline-flex rounded-md shadow-sm flex-wrap" role="group">
      {buttons}
    </div>
  );
};
